import { useCallback, useEffect, useState } from 'react';
import { Icon } from '@iconify/react';
import { Button } from '@/components/shadcn/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/shadcn/card';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/shadcn/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/shadcn/select';
import showToast from '@/components/ui/Toast';
import WhatsappMessageList, {
  type WhatsappMessageRow,
} from '@/components/community/whatsapp/WhatsappMessageList';
import whatsappCommunityService from '@/services/whatsappCommunityService';

const STATUS_ALL = 'all';

const STATUS_OPTIONS = [
  { value: STATUS_ALL, label: 'Semua status' },
  { value: 'pending', label: 'Pending' },
  { value: 'sent', label: 'Sent' },
  { value: 'failed', label: 'Failed' },
  { value: 'cancelled', label: 'Cancelled' },
];

const WhatsappMessagesTab = () => {
  const [messages, setMessages] = useState<WhatsappMessageRow[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(false);
  const [statusFilter, setStatusFilter] = useState(STATUS_ALL);
  const [selectedContent, setSelectedContent] = useState<string | null>(null);

  const loadMessages = useCallback(
    async (p: number, append: boolean) => {
      setIsLoading(true);
      try {
        const res = await whatsappCommunityService.listMessages({
          page: p,
          limit: 20,
          status: statusFilter === STATUS_ALL ? undefined : statusFilter,
        });
        const mapped: WhatsappMessageRow[] = res.messages.map((m, i) => ({
          ...m,
          _no: append ? 0 : i + 1,
        }));
        if (append) {
          setMessages((prev) => {
            const start = prev.length;
            return [...prev, ...mapped.map((m, i) => ({ ...m, _no: start + i + 1 }))];
          });
        } else {
          setMessages(mapped);
        }
        setPage(res.pagination.page || p);
        setHasMore((res.pagination.page || p) < (res.pagination.totalPages || 1));
      } catch (error) {
        if (!append) setMessages([]);
        setHasMore(false);
        showToast.error(
          error instanceof Error ? error.message : 'Gagal memuat pesan WhatsApp'
        );
      } finally {
        setIsLoading(false);
      }
    },
    [statusFilter]
  );

  useEffect(() => {
    void loadMessages(1, false);
  }, [loadMessages]);

  return (
    <div className="space-y-4">
      <Card className="rounded-2xl border-[#E0E6ED] shadow-sm">
        <CardHeader className="space-y-3 p-4 sm:p-5">
          <CardTitle className="text-base font-semibold text-[#0E1726]">Riwayat Pesan</CardTitle>
          <div className="flex items-center gap-2">
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger
                className="h-10 flex-1 rounded-xl sm:max-w-[180px]"
                aria-label="Filter status"
              >
                <SelectValue placeholder="Semua status" />
              </SelectTrigger>
              <SelectContent>
                {STATUS_OPTIONS.map((opt) => (
                  <SelectItem key={opt.value} value={opt.value}>
                    {opt.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Button
              type="button"
              variant="outline"
              className="h-10 shrink-0 rounded-xl"
              onClick={() => void loadMessages(1, false)}
            >
              <Icon icon="mdi:refresh" className="h-4 w-4" />
              <span className="hidden sm:inline">Refresh</span>
            </Button>
          </div>
        </CardHeader>

        <CardContent className="p-4 pt-0 sm:p-5 sm:pt-0">
          <WhatsappMessageList
            messages={messages}
            isLoading={isLoading}
            hasMore={hasMore}
            onLoadMore={() => void loadMessages(page + 1, true)}
            onViewMessage={setSelectedContent}
          />
        </CardContent>
      </Card>

      <Dialog open={!!selectedContent} onOpenChange={(open) => !open && setSelectedContent(null)}>
        <DialogContent className="max-w-md rounded-2xl">
          <DialogHeader>
            <DialogTitle>Pesan lengkap</DialogTitle>
            <DialogDescription className="whitespace-pre-wrap text-left text-sm text-[#0E1726]">
              {selectedContent}
            </DialogDescription>
          </DialogHeader>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default WhatsappMessagesTab;
